import {EventEmitter, Injectable} from '@angular/core';
import {ColorMapService} from '../../common/colors/color-map.service';
import {CombinedOrderElementDto} from "../models/core/combined-order-element-dto";
import {EmployeeCostDto} from "../../../../models/employee-cost-dto";
import {ErrorCodes} from "../../common/enums/states/error-codes";
import {RevenueCalculationStrategy} from "../../common/enums/strategies/revenue-calculation-strategy";
import {AnalyticsHttpService} from "../../common/http/analytics-http.service";
import {EmployeeHttpService} from "../../../../http-requests/employee-http.service";
import {CompositionTypeHttpService} from "../../../../http-requests/composition-type-http.service";
import {LocationHttpService} from "../../../../http-requests/location-http.service";
import {forkJoin} from "rxjs";
import {LocationDto} from "../../../../models/location-dto";
import {CompositionTypeNl} from "../../../../models/composition-type/composition-type-nl";
import {MultiEventAnalysisEventDto} from "../models/core/multi-event-analysis-event-dto";
import {RevenueCalculationMethod} from "../utils/revenue-calculation-method";
import {LinkedDtoRepository} from "../../common/repositories/linked-dto-repository";
import {EmploymentCostRepository} from "../repositories/core/employment-cost/employment-cost-repository";
import {TimeRepository} from "../repositories/core/time/time-repository";
import {OrderRepository} from "../repositories/core/orders/order-repository";
import {CategoryRevenueRepository} from "../repositories/derived/category-revenue/category-revenue-repository";
import {FilterVO} from "../../common/utils/filter/models/filter-vo";
import {EventRepository} from "../repositories/core/event/event-repository";
import {PaymentMethodTranslation} from "../../../../mappings/payment-method-translation";
import {FilterKeys} from "../../common/utils/filter/enums/filter-keys";
import {GlobalAlertService} from "../../../../_services/global-alert.service";
import {AlertLevel} from "../../../../enums/alert-level";

@Injectable()
export class TimeSeriesPerformanceStoreService {
  // Chart Datasets

  // Calculation Strategy

  revenueFn!: (combinedOrderElementDto: CombinedOrderElementDto) => number

  revenueCalculationMethodOption: RevenueCalculationStrategy = RevenueCalculationStrategy.revenue;


  // Filter / toggles
  isEmployeeCostsIncluded = true
  isNetRevenue = false
  isBookingTypeEmployeeSalesIncluded = false

  // Signals
  updateNotifier: EventEmitter<any> = new EventEmitter<any>()
  initializationCompletedNotifier: EventEmitter<any> = new EventEmitter<any>()

  analysisErrorState?: ErrorCodes = undefined
  containsNegativeRevenue: boolean = false


  constructor(private colorMapService: ColorMapService,
              private analyticsHttpService: AnalyticsHttpService,
              private locationHttpService: LocationHttpService,
              private compositionTypeHttpService: CompositionTypeHttpService,
              private employeeHttpService: EmployeeHttpService,
              public employmentCostRepository: EmploymentCostRepository,
              public timeRepository: TimeRepository,
              public eventRepository: EventRepository,
              public orderRepository: OrderRepository,
              // Todo chage generic type
              public linkedDtoRepository: LinkedDtoRepository<any>,
              private categoryRevenueRepository: CategoryRevenueRepository,
              private globalAlertService: GlobalAlertService) {

    this.analysisErrorState = ErrorCodes.loading
    this.revenueFn = RevenueCalculationMethod.fnMapping[this.revenueCalculationMethodOption]

    if (!this.loadTime())
      return

    this.fetchData()

  }

  loadTime() {
    let errorState = this.timeRepository.initialize()
    if (errorState !== undefined) {
      this.analysisErrorState = errorState
      return false
    }
    return true
  }

  private fetchData() {
    // Todo: create a repository and a domain service for this
    let sources = [
      this.locationHttpService.list(),
      this.compositionTypeHttpService.ListNl(),
      this.analyticsHttpService.fetchEventData(this.timeRepository.startAsISOString(), this.timeRepository.endAsISOString()),
      this.employeeHttpService.getEmployeeCostsFromTimerange(this.timeRepository.startAsISOString(), this.timeRepository.endAsISOString())
    ]


    forkJoin(sources).subscribe(([locations, compositions, multiEventAnalysis, employmentCosts]) => {


        this.initializeExpenses(employmentCosts as { [id: string]: EmployeeCostDto[] });
        if (!CompositionTypeNl.containsDefault(compositions as CompositionTypeNl[])) {
          (compositions as CompositionTypeNl[]).push(CompositionTypeNl.default())
        }
        this.initialize(multiEventAnalysis as MultiEventAnalysisEventDto[], locations as LocationDto[], compositions as CompositionTypeNl[])

      }, error => {
        this.analysisErrorState = ErrorCodes.serverError
      this.globalAlertService.createAndShow("Fehler", "Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.", AlertLevel.error);
      }
    )
  }

  initialize(multiEventAnalysisEventDto: MultiEventAnalysisEventDto[],
             locationDto: LocationDto[],
             compositionTypeNl: CompositionTypeNl[]
  ) {
    // 1. Initialize the event repository
    this.eventRepository.initialize(multiEventAnalysisEventDto)
    this.linkedDtoRepository.setDtoData("locationId", locationDto)
    this.linkedDtoRepository.setDtoData("compositionTypeId", compositionTypeNl)
    // Todo: SingleEventAnalysisUtils refactor
    this.linkedDtoRepository.setDtoDataFromDictionary("bookingType", PaymentMethodTranslation.paymentMethodToModelDict())


    // 2. Initialize the order repository
    this.orderRepository.initialize(multiEventAnalysisEventDto)


    this.initializationCompletedNotifier.emit()
    this.update()
  }


  initializeExpenses(employmentCosts: { [id: string]: EmployeeCostDto[] }) {
    this.employmentCostRepository.initialize(employmentCosts)
  }

  update() {
    this.analysisErrorState = ErrorCodes.loading

    // Todo: Optimization Goal - only update certain parts
    this.revenueFn = RevenueCalculationMethod.fnMapping[this.revenueCalculationMethodOption]

    // needs to be run before any other data is processed
    this.orderRepository.update()
    this.employmentCostRepository.update()


    // must be run before chart data is processed
    this.updateCategoryRevenueMap()


    // remove this
    //this.negativeRevenue = this.containsNegativePositions()
    this.analysisErrorState = undefined
    this.updateNotifier.emit()
  }//http://localhost:4200/analytics/(analytics:multi-event-analysis/revenue-performance-tracker)?startTime=1688169600&endTime=1690761600

  updateCategoryRevenueMap() {
    // Todo: Optimization Goal - Not every category needs to be updated instantly
    this.categoryRevenueRepository.updateCategoryRevenue("locationId", this.revenueFn)
    this.categoryRevenueRepository.updateCategoryRevenue("bookingType", this.revenueFn)
    this.categoryRevenueRepository.updateCategoryRevenue("compositionTypeId", this.revenueFn)
  }


  // region Filtering
  addFilter<T>(filterVO: FilterVO<T>, skipUpdate: boolean = false) {
    this.orderRepository.filter(filterVO, true)
    if (!skipUpdate)
      this.update()
  }

  removeFilter<T>(filterVO: FilterVO<T>, skipUpdate: boolean = false) {
    this.orderRepository.filter(filterVO, false)
    if (!skipUpdate)
      this.update()
  }

  addEmployeeCosts<T>(filterVO: FilterVO<T>, skipUpdate: boolean = false) {
    if (!this.employmentCostRepository.filterEmployeeCost(filterVO, true))
      return
    if (!skipUpdate)
      this.update()
  }

  removeEmployeeCosts<T>(filterVO: FilterVO<T>, skipUpdate: boolean = false) {
    if (!this.employmentCostRepository.filterEmployeeCost(filterVO, false))
      return
    if (!skipUpdate)
      this.update()
  }

  // endregion

  getStatisticsOverviewOption(option: keyof CombinedOrderElementDto): { id: string, revenue: number }[] {
    return this.categoryRevenueRepository.categoryRevenue[option]
  }

  toggleBookingTypeEmployeeSalesFilter(isExcluded: boolean) {
    this.isBookingTypeEmployeeSalesIncluded = isExcluded
    if (this.isBookingTypeEmployeeSalesIncluded)
      this.addFilter(new FilterVO(FilterKeys.BookingType, 2))
    else
      this.removeFilter(new FilterVO(FilterKeys.BookingType, 2))
  }

  toggleEmployeeCosts(isIncluded: boolean) {
    this.isEmployeeCostsIncluded = isIncluded
    // toggle not a full update is needed
    this.update()
  }


  getStartTime() {
    if (this.timeRepository.startTime === undefined)
      return new Date()
    return this.timeRepository.startTime
  }

  getEndTime() {
    if (this.timeRepository.endTime === undefined)
      // Set Date to 1970
      return new Date()
    return this.timeRepository.endTime
  }

  getRevenue() {
    return this.orderRepository.totalRevenue
  }

  getNetRevenue() {
    return this.orderRepository.totalRevenueAfterTax
  }

  getTaxes() {
    return this.orderRepository.totalTaxes
  }

  getQuantity() {
    return this.orderRepository.totalQuantity
  }

  getEventCount() {
    return this.eventRepository.events.length
  }

}
